import React from "react";

import { InfoOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Popover, Stack } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useDateFormatSelect } from "store";

import { MaterialTooltip } from "components/tooltips/MaterialTooltip";

export type AmortizationDateRangeFilterPopoverProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  appliedFrom: string | null;
  appliedTo: string | null;
  appliedHedgingDate: string | null;
  onApply: (from: string | null, to: string | null, hedgingDate: string | null) => void;
  onClear: () => void;
};

export const AmortizationDateRangeFilterPopover: React.FC<AmortizationDateRangeFilterPopoverProps> = ({
  anchorEl,
  onClose,
  appliedFrom,
  appliedTo,
  appliedHedgingDate,
  onApply,
  onClear
}) => {
  const { t } = useTranslation("debt");
  const dateFormat = useDateFormatSelect();
  const [draftFrom, setDraftFrom] = React.useState<Dayjs | null>(null);
  const [draftTo, setDraftTo] = React.useState<Dayjs | null>(null);
  const [draftHedgingDate, setDraftHedgingDate] = React.useState<Dayjs | null>(null);

  React.useEffect(() => {
    if (anchorEl) {
      setDraftFrom(appliedFrom ? dayjs(appliedFrom) : null);
      setDraftTo(appliedTo ? dayjs(appliedTo) : null);
      setDraftHedgingDate(appliedHedgingDate ? dayjs(appliedHedgingDate) : dayjs());
    }
  }, [anchorEl, appliedFrom, appliedTo, appliedHedgingDate]);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: { sx: { p: 3, width: 320 } }
      }}
    >
      <Stack spacing={2}>
        <DesktopDatePicker
          label={t("amortization.dateFilter.from")}
          format={dateFormat}
          views={["year", "month", "day"]}
          value={draftFrom}
          onChange={(newValue) => setDraftFrom(newValue)}
          sx={{ width: "100%" }}
        />
        <DesktopDatePicker
          label={t("amortization.dateFilter.to")}
          format={dateFormat}
          views={["year", "month", "day"]}
          value={draftTo}
          onChange={(newValue) => setDraftTo(newValue)}
          sx={{ width: "100%" }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DesktopDatePicker
            label={t("amortization.dateFilter.hedgingDate")}
            format={dateFormat}
            views={["year", "month", "day"]}
            value={draftHedgingDate}
            onChange={(newValue) => setDraftHedgingDate(newValue)}
            sx={{ width: "100%" }}
          />
          <MaterialTooltip title={t("amortization.dateFilter.hedgingDateTooltip")} isShow>
            <IconButton sx={{ padding: 0, width: 20, height: 20 }}>
              <InfoOutlined sx={{ fontSize: 16, color: "#496682" }} />
            </IconButton>
          </MaterialTooltip>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Button
            variant="text"
            onClick={() => {
              setDraftFrom(null);
              setDraftTo(null);
              setDraftHedgingDate(dayjs());
              onClear();
              onClose();
            }}
          >
            {t("amortization.dateFilter.clear")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onApply(
                draftFrom ? draftFrom.format("YYYY-MM-DD") : null,
                draftTo ? draftTo.format("YYYY-MM-DD") : null,
                draftHedgingDate ? draftHedgingDate.format("YYYY-MM-DD") : null
              );
              onClose();
            }}
          >
            {t("common.apply")}
          </Button>
        </Box>
      </Stack>
    </Popover>
  );
};
